import { useState, useRef } from 'react';
import './App.css';
import WelcomeScreen from './pages/WelcomeScreen';
import Login from './pages/Login';
import Barra from './components/Barra';
import DynamicIsland from './components/DynamicIsland';
import DynamicIslandMobile from './components/DynamicIslandMobile';
import POS from './pages/POS';
import ComanderoPocket from './pages/ComanderoPocket';
import TutorialPOS from './components/TutorialPOS';
import Inicio from './pages/Inicio';
import GestionDiaria from './pages/GestionDiaria';
import RegistroVentas from './pages/RegistroVentas';
import GestionPedidos from './pages/GestionPedidos';
import GestionArticulos from './pages/GestionArticulos';
import CategoryPromoVariants from './pages/CategoryPromoVariants';
import ChatClientes from './pages/ChatClientes';
import AnaliticasVentas from './pages/AnaliticasVentas';
import InventarioItems from './pages/InventarioItems';
import Gastos from './pages/Gastos';
import ManagerSettings from './pages/ManagerSettings';
import AdminBarra from './pages/AdminBarra';
import AdminCocina from './pages/AdminCocina';
import ReporteIncidencias from './pages/ReporteIncidencias';
import { canAccess, firstAllowedPage, clearSession } from './api/client';

/**
 * El usuario es válido pero el Manager no le tiene asignado ningún módulo. No
 * se le cae en el punto de venta por defecto: se le dice qué pasa.
 */
function SinVistas() {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-6">
      <p className="text-white text-lg">No tienes vistas asignadas</p>
      <p className="text-white/50 text-sm max-w-sm">
        Pídele a un administrador que te autorice al menos un módulo de esta sucursal.
      </p>
    </div>
  );
}

function App() {
  const [showWelcome, setShowWelcome] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activePage, setActivePage] = useState('inicio');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const dynamicIslandRef = useRef(null);

  if (showWelcome) return <WelcomeScreen onReady={() => setShowWelcome(false)} />;
  if (!isLoggedIn) {
    return (
      <Login
        onLogin={() => {
          // Recién ahora hay sesión, y con ella las vistas del usuario. El punto
          // de partida es la primera página que le autorizaron, no `inicio` fijo:
          // quien no tenga el punto de venta no debe caer dentro.
          setActivePage(firstAllowedPage());
          setIsLoggedIn(true);
        }}
      />
    );
  }

  // Ocultar un botón no impide llegar a la página: las alertas también navegan.
  // Por eso la navegación se filtra aquí, y no solo en la barra.
  const navigate = (page) => setActivePage(canAccess(page) ? page : firstAllowedPage());
  const page = canAccess(activePage) ? activePage : firstAllowedPage();

  // El comandero de bolsillo es una experiencia independiente para el mesero:
  // pantalla completa de celular, sin la barra de navegación del mostrador.
  // Su única salida es cerrar sesión.
  if (page === 'comandero') {
    const handleLogout = () => {
      clearSession();
      setIsLoggedIn(false);
    };
    return (
      <div className="h-[100dvh] bg-black overflow-hidden">
        <div
          className="fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-3"
          style={{ top: 'calc(10px + env(safe-area-inset-top, 0px))' }}
        >
          <DynamicIslandMobile ref={dynamicIslandRef} />
        </div>
        <ComanderoPocket
          onLogout={handleLogout}
          onOrderSuccess={() => dynamicIslandRef.current?.addAlert('success', 'Comanda enviada a caja')}
          notify={(type, msg) => {
            if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
            else dynamicIslandRef.current?.addAlert(type, msg);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-black overflow-hidden">
      <div className="fixed top-2 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
        <DynamicIsland ref={dynamicIslandRef} />
      </div>
      <Barra active={page} onNavigate={navigate} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} onTutorial={() => setShowTutorial(true)} />
      <div className="flex-1 min-h-0 overflow-hidden">
        {page === 'inicio' && <POS
          onOrderSuccess={() => dynamicIslandRef.current?.addAlert('success', 'Pedido cargado correctamente')}
          notify={(type, msg) => {
            if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
            else dynamicIslandRef.current?.addAlert(type, msg);
          }}
        />}
        {page === 'calendario' && <GestionDiaria notify={(type, msg) => {
          if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
          else dynamicIslandRef.current?.addAlert(type, msg);
        }} />}
        {page === 'finanzas' && <RegistroVentas notify={(type, msg) => {
          if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
          else dynamicIslandRef.current?.addAlert(type, msg);
        }} />}
        {page === 'ordenes' && <GestionPedidos dynamicIslandRef={dynamicIslandRef} />}
        {page === 'productos' && <GestionArticulos dynamicIslandRef={dynamicIslandRef} />}
        {page === 'categorias' && <CategoryPromoVariants dynamicIslandRef={dynamicIslandRef} />}
        {page === 'mensajes' && <ChatClientes />}
        {page === 'reportes' && <AnaliticasVentas />}
        {page === 'inventario' && <InventarioItems notify={(type, msg) => {
          if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
          else dynamicIslandRef.current?.addAlert(type, msg);
        }} />}
        {page === 'gastos' && <Gastos notify={(type, msg) => {
          if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
          else dynamicIslandRef.current?.addAlert(type, msg);
        }} />}
        {page === 'configuracion' && <ManagerSettings notify={(type, msg) => {
          if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
          else dynamicIslandRef.current?.addAlert(type, msg);
        }} />}
        {page === 'barra' && <AdminBarra notify={(type, msg) => {
          if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
          else dynamicIslandRef.current?.addAlert(type, msg);
        }} />}
        {page === 'cocina' && <AdminCocina notify={(type, msg) => {
          if (type === 'clear') dynamicIslandRef.current?.clearAlerts();
          else dynamicIslandRef.current?.addAlert(type, msg);
        }} />}
        {page === 'reporte' && <ReporteIncidencias />}
        {page === null && <SinVistas />}
        {page !== null && page !== 'inicio' && page !== 'calendario' && page !== 'finanzas' && page !== 'ordenes' && page !== 'productos' && page !== 'categorias' && page !== 'mensajes' && page !== 'reportes' && page !== 'inventario' && page !== 'gastos' && page !== 'configuracion' && page !== 'barra' && page !== 'cocina' && page !== 'reporte' && <Inicio />}
      </div>

      <TutorialPOS
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
      />
    </div>
  );
}

export default App;
